import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { getSocketBaseUrl } from '../config/socketUrl';
import { sanitizeHttpHeaderValue } from '../utils/httpHeaders';

const SocketContext = createContext(null);

/**
 * Socket.IO só carrega quando explicitamente pedido (evita engine.io/fetch e o erro do browser:
 * "Request with a GET or HEAD method cannot have a body").
 * Para ligar em dev: REACT_APP_ENABLE_SOCKET=1 no .env.local (com backend na mesma máquina/rede).
 */
function mustSkipSocketIo() {
  if (process.env.REACT_APP_DISABLE_SOCKET === '1') return true;
  if (process.env.REACT_APP_ENABLE_SOCKET !== '1') return true;
  const ws = process.env.REACT_APP_WS_URL;
  if (ws && String(ws).includes('workers.dev')) return true;
  // Build de produção sem REACT_APP_WS_URL: hosting estático (Pages) não tem Socket.IO em :3001
  if (process.env.NODE_ENV === 'production' && !ws) return true;
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const h = window.location.hostname;
    if (h.endsWith('.pages.dev') || h.endsWith('.cloudflarepages.com')) return true;
  }
  return false;
}

export const SocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [iaFeedback, setIaFeedback] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const skipRealtime = mustSkipSocketIo();
  const socketRef = useRef(null);

  useEffect(() => {
    if (skipRealtime) {
      setSocket(null);
      setIsConnected(false);
      return undefined;
    }

    if (!user || !token) {
      setSocket(null);
      setIsConnected(false);
      return undefined;
    }

    const socketBaseUrl = getSocketBaseUrl();
    const roomUserId = String(user.id);
    let cancelled = false;

    if (process.env.NODE_ENV === 'development') {
      console.log('[Socket.IO] ligando a', socketBaseUrl, 'room user id:', roomUserId);
    }

    import('socket.io-client')
      .then(({ io }) => {
        if (cancelled) return;
        // Só WebSocket evita engine.io polling-fetch (GET + body → erro em alguns browsers).
        // Para voltar ao fallback HTTP: REACT_APP_SOCKET_ALLOW_POLLING=1
        const allowPolling = process.env.REACT_APP_SOCKET_ALLOW_POLLING === '1';
        const newSocket = io(socketBaseUrl, {
          query: { token: sanitizeHttpHeaderValue(token) },
          transports: allowPolling ? ['websocket', 'polling'] : ['websocket'],
        });
        socketRef.current = newSocket;

        newSocket.on('connect', () => {
          console.log('Socket connected');
          setIsConnected(true);
          newSocket.emit('join_room', roomUserId);
        });

        newSocket.on('disconnect', () => {
          console.log('Socket disconnected');
          setIsConnected(false);
        });

        newSocket.on(`feedback_ia_${roomUserId}`, (feedback) => {
          console.log('IA Feedback received:', feedback);
          setIaFeedback(feedback);
        });

        newSocket.on('connect_error', (err) => {
          console.error('Socket connection error:', err);
        });

        setSocket(newSocket);
      })
      .catch((err) => console.error('Falha ao carregar socket.io-client:', err));

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user, token, skipRealtime]);

  const clearFeedback = () => {
    setIaFeedback(null);
  };

  const value = {
    socket,
    isConnected,
    iaFeedback,
    clearFeedback,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
